///
/// EarthBrowser
///
/// Created by Matt Giger on 4/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useState, useCallback, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogOverlay, DialogPortal, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { setOnboardingOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { markOnboardingComplete } from "@/hooks/useOnboarding"
import { useGetUserProfileQuery } from "@/redux/userApi"
import { ArrowLeft, ArrowRight, SkipForward } from "lucide-react"
import { onboardingSteps, TOTAL_STEPS } from "./onboardingSteps"
import { OnboardingStep } from "./OnboardingStep"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { track } from "@/utils/analytics"

const BETA_MODE = import.meta.env.VITE_BETA_MODE === "true"

export function OnboardingDialog() {
	const dispatch = useAppDispatch()
	const onboardingOpen = useAppSelector((state) => state.dialog.onboardingOpen)
	const { data: userProfile } = useGetUserProfileQuery()
	const [currentStep, setCurrentStep] = useState(0)
	const [direction, setDirection] = useState<"forward" | "backward">("forward")
	const [isAnimating, setIsAnimating] = useState(false)
	const maxStepReached = useRef(0)

	useEffect(() => {
		if (onboardingOpen) {
			setCurrentStep(0)
			setDirection("forward")
			maxStepReached.current = 0
			track("onboarding_started", {})
		}
	}, [onboardingOpen])

	const handleClose = useCallback(() => {
		dispatch(setOnboardingOpen(false))

		if (userProfile?.id) {
			markOnboardingComplete(userProfile.id)
		}

		const completed = currentStep === TOTAL_STEPS - 1
		try {
			if (completed) {
				track("onboarding_completed", { steps_viewed: maxStepReached.current + 1 })
			} else {
				track("onboarding_skipped", { skipped_at_step: currentStep, steps_viewed: maxStepReached.current + 1 })
			}
		} catch {}
	}, [dispatch, currentStep, userProfile])

	const goTo = useCallback((step: number) => {
		if (isAnimating) return
		setDirection(step > currentStep ? "forward" : "backward")
		setIsAnimating(true)
		setTimeout(() => {
			setCurrentStep(step)
			maxStepReached.current = Math.max(maxStepReached.current, step)
			setIsAnimating(false)
		}, 150)
	}, [currentStep, isAnimating])

	const handleNext = useCallback(() => {
		if (currentStep < TOTAL_STEPS - 1) {
			goTo(currentStep + 1)
		} else {
			handleClose()
		}
	}, [currentStep, goTo, handleClose])

	const handleBack = useCallback(() => {
		if (currentStep > 0) {
			goTo(currentStep - 1)
		}
	}, [currentStep, goTo])

	const step = onboardingSteps[currentStep]
	const isFirstStep = currentStep === 0
	const isLastStep = currentStep === TOTAL_STEPS - 1

	return (
		<Dialog open={onboardingOpen} onOpenChange={(open) => !open && handleClose()}>
		<DialogPortal>
			<DialogOverlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

			<DialogPrimitive.Content
				className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] overflow-hidden p-0 w-[90vw] max-w-lg rounded-3xl border-2 border-slate-700 focus:outline-none"
				aria-describedby="onboarding-description"
			>
				<div className="relative bg-background/95 backdrop-blur-xl shadow-2xl overflow-y-auto max-h-[85vh]">
					{/* Hidden accessible title + description */}
					<DialogTitle className="sr-only">EarthBrowser Onboarding</DialogTitle>
					<DialogDescription id="onboarding-description" className="sr-only">
						A guided tour of EarthBrowser&apos;s features
					</DialogDescription>

					{/* Progress dots */}
					<div className="flex items-center justify-center gap-2 pt-6 pb-2">
						{onboardingSteps.map((_, i) => (
							<button
								key={i}
								onClick={() => goTo(i)}
								className={`rounded-full transition-all duration-300 ${
									i === currentStep
										? "w-6 h-2 bg-gradient-to-r from-blue-500 to-cyan-500"
										: i < currentStep
											? "w-2 h-2 bg-cyan-500/60 hover:bg-cyan-500/80"
											: "w-2 h-2 bg-slate-600 hover:bg-slate-500"
								}`}
								aria-label={`Go to step ${i + 1}: ${onboardingSteps[i].title}`}
							/>
						))}
					</div>

					{/* Step counter */}
					<p className="text-center text-xs text-muted-foreground mb-4">
						{currentStep + 1} of {TOTAL_STEPS}
					</p>

					{/* Step content with animation */}
					<div className="px-6 pb-4">
						<div
							className={`transition-all duration-150 ease-in-out ${
								isAnimating
									? direction === "forward"
										? "opacity-0 translate-x-4"
										: "opacity-0 -translate-x-4"
									: "opacity-100 translate-x-0"
							}`}
						>
							{step && (
								<OnboardingStep step={step} isBeta={BETA_MODE} />
							)}
						</div>
					</div>

					{/* Navigation buttons */}
					<div className="flex items-center justify-between px-6 pb-6 pt-2">
						{/* Left side: Skip or Back */}
						<div className="flex-1">
							{isFirstStep ? (
								<Button
									variant="ghost"
									size="sm"
									onClick={handleClose}
									className="rounded-full text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
								>
									<SkipForward className="h-4 w-4 mr-1" />
									<span>Skip</span>
								</Button>
							) : (
								<Button
									variant="outline"
									size="sm"
									onClick={handleBack}
									className="rounded-full border-slate-700 hover:bg-slate-700/50"
								>
									<ArrowLeft className="h-4 w-4 mr-1" />
									<span>Back</span>
								</Button>
							)}
						</div>

						{/* Right side: Next or Get Started */}
						<div className="flex-1 flex justify-end">
							<Button
								size="sm"
								onClick={handleNext}
								className="rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
							>
								<span>{isLastStep ? "Get Started" : "Next"}</span>
								{!isLastStep && <ArrowRight className="h-4 w-4 ml-1" />}
							</Button>
						</div>
					</div>
				</div>
			</DialogPrimitive.Content>
		</DialogPortal>
		</Dialog>
	)
}
